// Wejścia przy przewijaniu: nagłówki sekcji (słowa + etykieta + lasso), karty kaskadą z dodatkami, galeria i polaroidy.
import {gsap, ScrollTrigger, SplitText, $$, has} from "./shared";
import {tickText} from "./counters";

const CARDS = [
    ".cc-feature", ".cc-dog", ".cc-puppy", ".cc-litter", ".cc-plan", ".cc-stat", ".cc-contact", ".cc-form",
    ".cc-faq__item", ".cc-announce:not(.cc-announce--bar)", ".cc-health", ".cc-pedigree", ".cc-pack__text", ".cc-prose",
    ".cc-pricing__note"
].join(",");

/** Dodatki po wejściu karty danego typu (klucz = klasa karty). */
const EXTRAS = {
    "cc-feature": (card) => pop(card.querySelector(".cc-feature__icon"), {scale: 0, rotation: -120, duration: 1, delay: 0.25, ease: "elastic.out(1, 0.5)"}),
    "cc-puppy": (card) => {
        pop(card.querySelector(".cc-puppy__collar"), {scale: 0, duration: 0.9, delay: 0.3, ease: "elastic.out(1, 0.4)"});
        pop(card.querySelector(".cc-puppy__status"), {y: -14, autoAlpha: 0, duration: 0.5, delay: 0.4, ease: "back.out(3)"});
    },
    "cc-dog": (card) => {
        pop(card.querySelector(".cc-dog__role"), {y: -18, rotation: -12, autoAlpha: 0, duration: 0.6, delay: 0.3, ease: "back.out(3)"});
        pop(card.querySelectorAll(".cc-dog__chips > *"), {scale: 0, duration: 0.5, delay: 0.45, stagger: 0.06, ease: "back.out(3)"});
    },
    "cc-litter": (card) => pop(card.querySelector(".cc-litter__letter"), {scale: 0, rotation: -200, duration: 1.2, delay: 0.2, ease: "elastic.out(1, 0.5)"}),
    "cc-announce": (card) => pop(card.querySelector(".cc-announce__icon"), {scale: 0, rotation: -200, duration: 1.1, delay: 0.2, ease: "elastic.out(1, 0.5)"}),
    "cc-health": (card) => pop(card.querySelectorAll("tbody tr"), {x: -20, autoAlpha: 0, duration: 0.5, delay: 0.25, stagger: 0.07, ease: "power2.out"}),
    "cc-pedigree": (card) => pop(card.querySelectorAll(".cc-ped__cell"), {scale: 0.6, autoAlpha: 0, duration: 0.6, delay: 0.2, stagger: 0.05, ease: "back.out(2)"}),
    "cc-stat": (card, index) => {
        const sticker = card.querySelector(".cc-sticker");
        if (!sticker) {
            return;
        }
        pop(sticker, {scale: 0, rotation: -240, duration: 1.3, delay: 0.15, ease: "elastic.out(1, 0.5)"});
        tickText(sticker.querySelector(".cc-sticker__title"), {delay: 0.35, duration: 1.4});
        gsap.to(sticker, {y: -8, duration: 1.8 + index * 0.25, ease: "sine.inOut", yoyo: true, repeat: -1, delay: 1.6});
    },
    "cc-plan": (card) => {
        pop(card.querySelector(".cc-plan__badge"), {scale: 0, rotation: -40, y: -24, duration: 1, delay: 0.45, ease: "elastic.out(1, 0.45)"});
        tickText(card.querySelector(".cc-plan__amount"), {delay: 0.3, duration: 1.4});
        pop(card.querySelectorAll(".cc-plan__item"), {x: -16, autoAlpha: 0, duration: 0.45, delay: 0.35, stagger: 0.05, ease: "power2.out"});
        pop(card.querySelectorAll(".cc-plan__mark"), {scale: 0, duration: 0.5, delay: 0.45, stagger: 0.05, ease: "back.out(3)"});
    },
    "cc-contact": (card) => {
        const icon = card.querySelector(".cc-contact__phone-icon");
        if (!icon) {
            return;
        }
        // Telefon co chwilę „dzwoni”, ale tylko gdy karta jest na ekranie.
        const ring = gsap.to(icon, {rotation: 18, duration: 1, ease: "cc-ring", repeat: -1, repeatDelay: 3.2, delay: 0.8});
        ScrollTrigger.create({trigger: card, start: "top bottom", end: "bottom top", onToggle: (self) => (self.isActive ? ring.resume() : ring.pause())});
    }
};

function pop(target, vars) {
    return has(target) ? gsap.from(target, vars) : null;
}

/**
 * Elementy, które przy starcie są jeszcze pod ekranem – tylko one dostają ukrycie i wyzwalacz.
 *
 * Po odświeżeniu strony przeglądarka przywraca pozycję przewijania, więc startujemy np. przy formularzu
 * na samym dole i pół strony jest już za nami. Animowanie tego nie ma sensu, a co gorsza ScrollTrigger
 * wywala się wtedy na starcie: kilkanaście wyzwalaczy z „once” odpala się naraz i zabija samo siebie
 * w trakcie pierwszego odświeżenia, po którym biblioteka właśnie iteruje. Cały start animacji przerywa
 * się wtedy błędem, a karty zostają z wpisanym w styl „opacity: 0”.
 */
function below(elements, ratio) {
    const line = window.innerHeight * ratio;
    return elements.filter((el) => el.getBoundingClientRect().top > line);
}

/** Awaryjne odsłonięcie treści, gdyby wejścia mimo wszystko się wywróciły. */
export function revealAll() {
    gsap.set($$(CARDS).concat($$(".cc-photo--polaroid")), {clearProps: "opacity,visibility"});
    gsap.set($$(".cc-gallery__item"), {clearProps: "clipPath"});
}

/**
 * Przy skoku do kotwicy albo szybkim przewijaniu do „wejścia” trafia naraz mnóstwo elementów.
 * Te, które są już nad ekranem, pokazujemy od razu; widoczne dostają kaskadę o łącznej długości max 0,8 s.
 */
function sortBatch(batch) {
    const skipped = batch.filter((el) => el.getBoundingClientRect().bottom <= 0);
    if (skipped.length) {
        gsap.set(skipped, {autoAlpha: 1, clearProps: "clipPath"});
    }
    const visible = batch.filter((el) => !skipped.includes(el));
    return {visible, each: Math.min(0.12, 0.8 / Math.max(1, visible.length))};
}

export function initReveals() {
    initHeadings();
    initCards();
    initGallery();
    initPolaroids();
}

function initHeadings() {
    below($$(".cc-sh"), 0.86).forEach((heading) => {
        if (heading.closest(".cc-hero")) {
            return;
        }
        const title = heading.querySelector(".cc-sh__title");
        const eyebrow = heading.querySelector(".cc-eyebrow");
        const lead = heading.querySelector(".cc-sh__lead");
        const tl = gsap.timeline({
            scrollTrigger: {trigger: heading, start: "top 86%", once: true},
            defaults: {ease: "back.out(1.8)"}
        });
        if (eyebrow) {
            tl.from(eyebrow, {x: -24, autoAlpha: 0, duration: 0.5});
        }
        if (title) {
            const split = SplitText.create(title, {type: "words", tag: "span", wordsClass: "cc-word"});
            tl.from(split.words, {yPercent: 70, rotation: 6, autoAlpha: 0, duration: 0.8, stagger: 0.07}, "-=0.3");
            title.querySelectorAll(".cc-mark").forEach((mark) => {
                tl.fromTo(mark, {"--mark-size": "0%"}, {"--mark-size": "100%", duration: 0.55, ease: "power3.inOut"}, "-=0.5");
            });
            title.querySelectorAll(".cc-lasso").forEach((lasso) => {
                tl.fromTo(lasso, {"--lasso-draw": "0%"}, {"--lasso-draw": "100%", duration: 0.7, ease: "power2.inOut"}, "-=0.35");
            });
        }
        if (lead) {
            tl.from(lead, {y: 20, autoAlpha: 0, duration: 0.7, ease: "power3.out"}, "-=0.5");
        }
    });
}

function initCards() {
    const cards = below($$(CARDS).filter((card) => !card.closest(".cc-hero, .cc-profile")), 0.9);
    if (!cards.length) {
        return;
    }
    gsap.set(cards, {autoAlpha: 0});
    ScrollTrigger.batch(cards, {
        start: "top 90%",
        once: true,
        onEnter: (batch) => {
            const {visible, each} = sortBatch(batch);
            if (!visible.length) {
                return;
            }
            gsap.fromTo(visible,
                {y: 70, scale: 0.92, rotation: () => gsap.utils.random(-5, 5)},
                {y: 0, scale: 1, rotation: 0, autoAlpha: 1, duration: 1, stagger: each, ease: "back.out(1.5)"}
            );
            visible.forEach((card, i) => {
                const key = Object.keys(EXTRAS).find((name) => card.classList.contains(name));
                if (key) {
                    gsap.delayedCall(i * each, () => EXTRAS[key](card, i));
                }
            });
        }
    });
}

function initGallery() {
    // W mozaice zdjęcie zostaje lekko powiększone, żeby miało zapas na pływanie w ramce.
    const restScale = (img) => (img.closest(".cc-gallery--bento") ? 1.14 : 1);
    const tiles = below($$(".cc-gallery__item"), 0.94);
    if (tiles.length) {
        gsap.set(tiles, {clipPath: "inset(100% 0% 0% 0%)"});
        ScrollTrigger.batch(tiles, {
            start: "top 94%",
            once: true,
            onEnter: (batch) => {
                const {visible, each} = sortBatch(batch);
                if (!visible.length) {
                    return;
                }
                const stagger = Math.min(0.08, each);
                gsap.to(visible, {clipPath: "inset(0% 0% 0% 0%)", duration: 1.1, stagger, ease: "power4.out", clearProps: "clipPath"});
                const imgs = visible.map((tile) => tile.querySelector("img")).filter(Boolean);
                if (imgs.length) {
                    gsap.fromTo(imgs, {scale: 1.35}, {scale: (i, img) => restScale(img), duration: 1.4, stagger, ease: "power3.out"});
                }
            }
        });
    }

    // Mozaika: zdjęcia lekko pływają w ramkach przy przewijaniu – niezależnie od wejść.
    $$(".cc-gallery--bento .cc-gallery__item img").forEach((img) => {
        gsap.fromTo(img, {yPercent: -6}, {
            yPercent: 6,
            ease: "none",
            scrollTrigger: {trigger: img.closest(".cc-gallery__item"), start: "top bottom", end: "bottom top", scrub: true}
        });
    });
}

function initPolaroids() {
    const polaroids = below($$(".cc-photo--polaroid").filter((p) => !p.closest(".cc-hero, .cc-profile")), 0.88);
    if (!polaroids.length) {
        return;
    }
    gsap.set(polaroids, {autoAlpha: 0});
    ScrollTrigger.batch(polaroids, {
        start: "top 88%",
        once: true,
        onEnter: (batch) => {
            const {visible, each} = sortBatch(batch);
            if (!visible.length) {
                return;
            }
            gsap.fromTo(visible,
                {y: -90, rotation: () => gsap.utils.random(-25, 25)},
                {y: 0, rotation: 0, autoAlpha: 1, duration: 1.3, stagger: each, ease: "elastic.out(1, 0.55)"}
            );
            pop(visible.map((p) => p.querySelector(".cc-photo__tape")).filter(Boolean), {scaleX: 0, duration: 0.5, delay: 0.55, stagger: each, ease: "back.out(2)"});
        }
    });
}
